import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BackendApiService } from '../../../../../services/backend-api.service';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, map } from 'rxjs/operators';
import { TaskTag } from '../task-sidebar/task-sidebar.component';

@Component({
    selector: 'app-task-tag-selector-popup',
    templateUrl: './task-tag-selector-popup.component.html',
    styleUrls: ['./task-tag-selector-popup.component.scss'],
    standalone: false
})
export class TaskTagSelectorPopupComponent implements OnInit {

  // Available colors for new tags
  public availableColors = [
    { key: 'blue', name: 'Blue', bg: 'rgba(59, 130, 246, 0.2)', text: '#93c5fd' },
    { key: 'green', name: 'Green', bg: 'rgba(34, 197, 94, 0.2)', text: '#86efac' },
    { key: 'yellow', name: 'Yellow', bg: 'rgba(234, 179, 8, 0.2)', text: '#fde047' },
    { key: 'red', name: 'Red', bg: 'rgba(239, 68, 68, 0.2)', text: '#fca5a5' },
    { key: 'purple', name: 'Purple', bg: 'rgba(168, 85, 247, 0.2)', text: '#d8b4fe' },
    { key: 'orange', name: 'Orange', bg: 'rgba(249, 115, 22, 0.2)', text: '#fdba74' },
    { key: 'gray', name: 'Gray', bg: 'rgba(107, 114, 128, 0.2)', text: '#d1d5db' }
  ];

  public existingTags: TaskTag[] = [];
  public currentTaskTags: TaskTag[] = [];
  public tagSearchInput$ = new Subject<string>();
  public tagSearchResults$: Observable<TaskTag[]> = of([]);
  public tagSearchLoading = false;

  public showCreateNew = true;
  public newTagName = '';

  /**
   * Either a preset key ('blue', 'red', …) or a raw hex ('#3B82F6') chosen from
   * the picker. Both forms are resolved by getColorStyle(), so tags saved before
   * the picker existed keep rendering.
   */
  public newTagColor = 'blue';

  /** Hex bound to the native colour input and shown on the custom swatch. */
  public customColor = '#3B82F6';

  constructor(
    public dialogRef: MatDialogRef<TaskTagSelectorPopupComponent>,
    private backendApiService: BackendApiService,
    @Inject(MAT_DIALOG_DATA) public data: { currentTags: TaskTag[] }
  ) {
    this.currentTaskTags = data.currentTags || [];
  }

  ngOnInit(): void {
    this.setupTagSearch();
    this.loadAvailableTags();
  }

  /**
   * Setup tag search with debouncing
   */
  private setupTagSearch(): void {
    this.tagSearchResults$ = this.tagSearchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchTerm: string) => {
        if (!searchTerm || searchTerm.trim().length === 0) {
          this.tagSearchLoading = false;
          return of(this.existingTags);
        }

        this.tagSearchLoading = true;
        // Filter existing tags locally instead of API call
        const filtered = this.existingTags.filter(tag =>
          tag.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        this.tagSearchLoading = false;
        return of(filtered);
      })
    );
  }

  /**
   * Load all available system tags from backend
   * Uses user/tags endpoint with module_id=7 and is_system=1
   */
  private loadAvailableTags(): void {
    this.backendApiService.getApi('user/tags?module_id=7&is_system=1').subscribe(
      (response: any) => {
        const dataArray = response?.data || response;
        this.existingTags = this.transformTagsFromAPI(dataArray || []);

        // If no tags from API, use static test tags for testing
        if (this.existingTags.length === 0) {
          this.existingTags = this.getStaticTestTags();
        }
      },
      error => {
        console.error('Error loading tags:', error);
        // Use static test tags when API fails (for testing)
        this.existingTags = this.getStaticTestTags();
      }
    );
  }

  /**
   * Get static test tags for testing purposes
   * TODO: Remove this method once backend API is implemented
   * @returns Array of test TaskTag objects
   */
  private getStaticTestTags(): TaskTag[] {
    return [
      { id: 101, name: 'Urgent', color: 'red' },
      { id: 102, name: 'Bug Fix', color: 'orange' },
      { id: 103, name: 'Feature', color: 'blue' },
      { id: 104, name: 'Enhancement', color: 'green' },
      { id: 105, name: 'In Progress', color: 'yellow' },
      { id: 106, name: 'Review', color: 'purple' },
      { id: 107, name: 'Documentation', color: 'gray' },
      { id: 108, name: 'Testing', color: 'blue' },
      { id: 109, name: 'High Priority', color: 'red' },
      { id: 110, name: 'Low Priority', color: 'green' },
      { id: 111, name: 'Backend', color: 'orange' },
      { id: 112, name: 'Frontend', color: 'purple' }
    ];
  }

  /**
   * Transform API tags to TaskTag interface
   */
  private transformTagsFromAPI(apiTags: any[]): TaskTag[] {
    return apiTags.map(tag => ({
      id: tag.id,
      name: tag.tag || tag.name || tag.tag_name || tag.label,
      color: tag.color || 'blue'
    }));
  }

  /**
   * Check if tag is already added to task
   */
  public isTagSelected(tag: TaskTag): boolean {
    return this.currentTaskTags.some(t => t.id === tag.id || t.name === tag.name);
  }

  /**
   * Select an existing tag
   */
  public selectTag(tag: TaskTag): void {
    if (!this.isTagSelected(tag)) {
      this.dialogRef.close({ action: 'add', tag });
    }
  }

  /**
   * Toggle create new tag form
   */
  public toggleCreateNew(): void {
    this.showCreateNew = !this.showCreateNew;
    if (!this.showCreateNew) {
      this.newTagName = '';
      this.newTagColor = 'blue';
    }
  }

  /**
   * Create and select new tag
   */
  public createNewTag(): void {
    if (!this.newTagName.trim()) return;

    const newTag: TaskTag = {
      name: this.newTagName.trim(),
      color: this.newTagColor
    };

    this.dialogRef.close({ action: 'create', tag: newTag });
  }

  /**
   * Get color style for preview. Accepts either a preset key or a hex value —
   * preset keys keep their hand-tuned translucent bg / light text pair, while a
   * hex gets the same treatment derived from its channels.
   */
  public getColorStyle(colorValue: string): any {
    const preset = this.availableColors.find(c => c.key === colorValue);
    if (preset) {
      return { 'background-color': preset.bg, 'color': preset.text };
    }

    if (this.isHex(colorValue)) {
      const { r, g, b } = this.hexToRgb(colorValue);
      return {
        'background-color': `rgba(${r}, ${g}, ${b}, 0.2)`,
        'color': colorValue,
      };
    }

    return {};
  }

  /** True when the stored value is a hex colour rather than a preset key. */
  public isHex(value: string): boolean {
    return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test((value || '').trim());
  }

  /** Select one of the fixed preset swatches. */
  public selectPreset(key: string): void {
    this.newTagColor = key;
  }

  /** Called by the native colour input; moves the selection off the presets. */
  public onCustomColorChange(hex: string): void {
    this.customColor = hex;
    this.newTagColor = hex;
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    let value = hex.trim().replace('#', '');
    if (value.length === 3) {
      value = value.split('').map((c) => c + c).join('');
    }
    const int = parseInt(value, 16);
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
  }

  /**
   * Close dialog
   */
  public close(): void {
    this.dialogRef.close();
  }
}
